import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { PERKS, getPerk, TIER_COLORS, TIER_ORDER, type Perk } from '@/data/perks';

/**
 * PerksPage — dedicated `/perks` showcase.
 *
 * @description Rank-centric view of the server's reworked perk system. A responsive
 * grid selects a perk; the detail panel is driven by a prestige-rank ladder where
 * selecting a rank shows that rank's icon, the weapons it unlocks, the cumulative
 * arsenal and (later) the per-rank perk bonuses. Deep-linkable via `?perk=<key>`.
 *
 * @returns {JSX.Element} The custom perks page.
 */
export default function PerksPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialKey = useMemo(() => {
    const requested = searchParams.get('perk');
    return getPerk(requested)?.key ?? PERKS[0].key;
  }, [searchParams]);

  const [selectedKey, setSelectedKey] = useState<string>(initialKey);

  // Keep state in sync if the URL changes (e.g. back/forward navigation).
  useEffect(() => {
    const requested = searchParams.get('perk');
    const valid = getPerk(requested)?.key;
    if (valid && valid !== selectedKey) setSelectedKey(valid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const selectedPerk = getPerk(selectedKey) ?? PERKS[0];

  function selectPerk(key: string) {
    setSelectedKey(key);
    const next = new URLSearchParams(searchParams);
    next.set('perk', key);
    setSearchParams(next, { replace: true });
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <Navigation />
      <main className="relative z-10 pt-24 pb-16">
        <div className="container mx-auto px-4">
          {/* Back link + hero */}
          <div className="mb-10">
            <Link to="/" className="text-gray-400 hover:text-red-400 transition-colors text-sm mb-4 inline-block">
              <i className="ri-arrow-left-s-line"></i> Back to Home
            </Link>
            <div className="text-center">
              <h1 className="text-4xl md:text-6xl font-bold font-orbitron horror-heading">
                Custom <span className="text-red-500">Perks</span>
              </h1>
              <div className="w-24 h-1 bg-red-600 mx-auto my-6"></div>
              <p className="text-lg text-gray-300 max-w-3xl mx-auto">
                Every perk on Uliunai has been reworked from the ground up — with a 15-rank
                prestige system that rewards the grind with exclusive custom icons, from
                humble <span className="text-gray-200">Iron</span> all the way to the
                ultra-rare <span className="text-red-400">Atomic</span>.
              </p>
            </div>
          </div>

          {/* Perk selector — responsive grid, all perks visible (no horizontal scroll) */}
          <div className="mb-12 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {PERKS.map((perk) => {
              const active = perk.key === selectedKey;
              return (
                <button
                  key={perk.key}
                  onClick={() => selectPerk(perk.key)}
                  aria-pressed={active}
                  className={`group relative flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition-all duration-300 ${
                    active
                      ? 'border-red-500 bg-red-600/15 shadow-lg shadow-red-600/20'
                      : 'border-red-900/30 bg-black/60 hover:border-red-600/50 hover:bg-white/5'
                  }`}
                >
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center">
                    {perk.comingSoon ? (
                      <i className="ri-fire-line text-xl text-red-500"></i>
                    ) : (
                      <img
                        src={`/perks/icons/${perk.key}/celestial.webp`}
                        alt=""
                        loading="lazy"
                        className={`h-9 w-9 object-contain transition-transform duration-300 ${active ? 'scale-110' : 'group-hover:scale-110'}`}
                      />
                    )}
                  </span>
                  <span className={`min-w-0 flex-1 truncate font-orbitron text-sm font-bold ${active ? 'text-white' : 'text-gray-300 group-hover:text-white'}`}>
                    {perk.name}
                  </span>
                  {perk.comingSoon && (
                    <span className="rounded-full bg-red-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">
                      Soon
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Detail panel — keyed so it re-animates on perk switch */}
          <PerkDetail key={selectedPerk.key} perk={selectedPerk} />
        </div>
      </main>
      <Footer />
    </div>
  );
}

/** A prestige rank with its icon (if any) and level band. */
interface RankMeta {
  tier: string;
  label: string;
  src: string | null;
  minLevel: number;
  maxLevel: number;
  levelRange: string;
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * PerkDetail — rank-centric panel: a prestige ladder drives a unified rank card
 * (icon + level band + weapons unlocked + cumulative arsenal + bonuses slot).
 *
 * @param {object} props - Component props.
 * @param {Perk} props.perk - The perk to display.
 * @returns {JSX.Element} The perk detail panel.
 */
function PerkDetail({ perk }: { perk: Perk }) {
  const hasIcons = perk.icons.length > 0;
  const hasWeapons = perk.weapons.length > 0;

  // Unified rank list: real tier icons when available, else synthesized bands
  // (e.g. Firebug, which has weapons but no prestige art yet).
  const ranks: RankMeta[] = hasIcons
    ? perk.icons.map((t) => ({
        tier: t.tier, label: t.label, src: t.src,
        minLevel: t.minLevel, maxLevel: t.maxLevel, levelRange: t.levelRange,
      }))
    : TIER_ORDER.map((tier, i) => {
        const minLevel = i * 10 + 1;
        const maxLevel = minLevel + 9;
        return { tier, label: cap(tier), src: null, minLevel, maxLevel, levelRange: `${minLevel}-${maxLevel}` };
      });

  // Default to the top rank — best icon + full arsenal on first view.
  const [sel, setSel] = useState(ranks.length - 1);
  const rank = ranks[Math.min(sel, ranks.length - 1)];
  const color = TIER_COLORS[rank.label] ?? '#9ca3af';

  // Treat the starter weapon (unlock 0) as part of the first rank for binning.
  const eff = (lvl: number) => Math.max(lvl, 1);
  const newThisRank = perk.weapons.filter((w) => eff(w.unlockLevel) >= rank.minLevel && eff(w.unlockLevel) <= rank.maxLevel);
  const ownedSoFar = perk.weapons.filter((w) => w.unlockLevel <= rank.maxLevel);
  const upcoming = perk.weapons.length - ownedSoFar.length;
  const rankBonuses = perk.bonuses[Math.min(sel, perk.bonuses.length - 1)] ?? [];

  return (
    <div className="scroll-reveal revealed">
      {/* Perk header — icon reflects the selected rank */}
      <div className="mb-8 flex flex-col items-center gap-5 text-center sm:flex-row sm:text-left">
        <div
          className="flex h-28 w-28 flex-shrink-0 items-center justify-center rounded-2xl border bg-black/50"
          style={{ borderColor: `${color}55` }}
        >
          {rank.src ? (
            <img src={rank.src} alt={`${perk.name} ${rank.label}`} className="h-24 w-24 object-contain drop-shadow-[0_0_18px_rgba(220,38,38,0.35)]" />
          ) : (
            <i className="ri-fire-line text-5xl text-red-500"></i>
          )}
        </div>
        <div>
          <h2 className="text-4xl font-bold font-orbitron text-white">{perk.name}</h2>
          <p className="mt-1 text-lg text-red-400">{perk.tagline}</p>
          <p className="mt-3 max-w-2xl leading-relaxed text-gray-300">{perk.description}</p>
        </div>
      </div>

      {/* Progression ladder — all 15 ranks; selects the rank below */}
      <div className="mb-8">
        <div className="mb-3 flex items-center gap-3">
          <span className="text-xs uppercase tracking-widest text-gray-500">Progression</span>
          <div className="h-px flex-1 bg-gradient-to-r from-red-900/50 to-transparent"></div>
          <span className="text-xs text-gray-600">{ranks.length} ranks · 10 levels each</span>
        </div>
        <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2">
          {ranks.map((r, i) => {
            const on = i === sel;
            const c = TIER_COLORS[r.label] ?? '#9ca3af';
            return (
              <button
                key={r.tier}
                onClick={() => setSel(i)}
                onMouseEnter={() => setSel(i)}
                aria-pressed={on}
                title={`${r.label} · Lvl ${r.levelRange}`}
                className={`group flex w-[64px] flex-col items-center rounded-lg border p-2 transition-all duration-200 ${
                  on ? 'scale-110 bg-white/5' : 'hover:scale-105 hover:bg-white/5'
                }`}
                style={{ borderColor: on ? c : 'rgba(127,29,29,0.20)' }}
              >
                {r.src ? (
                  <img
                    src={r.src}
                    alt=""
                    loading="lazy"
                    className={`h-10 w-10 object-contain transition-all duration-200 ${on ? '' : 'opacity-70 grayscale group-hover:opacity-100 group-hover:grayscale-0'}`}
                  />
                ) : (
                  <span className="flex h-10 w-10 items-center justify-center">
                    <span className="h-4 w-4 rounded-full" style={{ backgroundColor: c }}></span>
                  </span>
                )}
                <span className={`mt-1 text-[10px] font-semibold leading-tight ${on ? 'text-white' : 'text-gray-500'}`}>{r.label}</span>
                <span className="text-[9px] tabular-nums text-gray-600">{r.levelRange}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Unified rank card */}
      <div className="overflow-hidden rounded-xl border bg-black/40" style={{ borderColor: `${color}55` }}>
        <div className="flex items-center gap-3 px-6 py-3" style={{ backgroundColor: `${color}14` }}>
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }}></span>
          <h3 className="font-orbitron text-xl font-bold text-white">{rank.label}</h3>
          <span className="rounded-full border px-2.5 py-0.5 text-xs font-semibold" style={{ borderColor: `${color}66`, color }}>
            Levels {rank.levelRange}
          </span>
        </div>

        <div className="grid gap-6 p-6 md:grid-cols-[8rem,1fr]">
          {/* Big rank icon */}
          <div className="flex justify-center md:justify-start">
            <div className="flex h-32 w-32 items-center justify-center rounded-xl border border-white/10 bg-white/5">
              {rank.src ? (
                <img src={rank.src} alt={`${perk.name} ${rank.label} tier icon`} className="h-28 w-28 object-contain drop-shadow-[0_0_16px_rgba(220,38,38,0.35)]" />
              ) : (
                <i className="ri-fire-line text-5xl text-red-500"></i>
              )}
            </div>
          </div>

          {/* Rank details */}
          <div className="space-y-5">
            {hasWeapons && (
              <div>
                <div className="mb-2 text-xs uppercase tracking-widest text-gray-500">New this rank</div>
                {newThisRank.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {newThisRank.map((w) => (
                      <span
                        key={w.class}
                        className="inline-flex items-center gap-2 rounded-lg border px-3 py-1.5"
                        style={{ borderColor: `${color}66`, backgroundColor: `${color}14` }}
                      >
                        <i className="ri-crosshair-2-line" style={{ color }}></i>
                        <span className="font-orbitron text-sm font-bold text-white">{w.name}</span>
                        {w.damage != null && <span className="text-[11px] text-gray-400">{w.damage} dmg</span>}
                        {w.isSpawn && (
                          <span className="rounded bg-black/40 px-1.5 text-[10px] font-medium text-red-300">spawns with</span>
                        )}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">No new weapon unlocks in this rank.</p>
                )}
              </div>
            )}

            {hasWeapons && (
              <div>
                <div className="mb-2 text-xs uppercase tracking-widest text-gray-500">
                  Arsenal so far <span className="text-gray-600">({ownedSoFar.length}/{perk.weapons.length})</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {ownedSoFar.map((w) => (
                    <span key={w.class} className="rounded-md border border-red-900/20 bg-black/50 px-2 py-1 text-xs text-gray-300">
                      {w.name}
                    </span>
                  ))}
                </div>
                {upcoming > 0 && (
                  <p className="mt-2 text-xs text-gray-600">+{upcoming} more unlock at higher ranks.</p>
                )}
              </div>
            )}

            {/* Per-rank bonuses — computed from the live perk code */}
            <div>
              <div className="mb-2 text-xs uppercase tracking-widest text-gray-500">Perk bonuses at this rank</div>
              {rankBonuses.length > 0 ? (
                <>
                  <ul className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
                    {rankBonuses.map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                        <i className="ri-check-line mt-0.5 flex-shrink-0" style={{ color }}></i>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-3 text-[11px] text-gray-600">
                    Reflects current in-game balance — more bonuses will appear as the perks are finalized.
                  </p>
                </>
              ) : (
                <div className="rounded-lg border border-dashed border-red-900/30 bg-black/30 p-4 text-sm text-gray-500">
                  <i className="ri-information-line mr-1.5 text-red-400/70"></i>
                  Per-rank bonus details are coming soon.
                </div>
              )}
            </div>

            {!hasWeapons && (
              <p className="text-sm text-gray-600">
                Weapon data for this perk isn't available yet.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
